import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { toast } from "../../../../components/common/Toast/toast";
import { getBatches, updateBatch } from "../../../../../services/BatchService.js";
import { checkClash } from "../../../../../services/ClassroomManagementService.js";
import Modal from "../../components/Modal.jsx";
import AsyncPagedSelect from "../../components/AsyncPagedSelect.jsx";
import DaysToggle from "../../components/DaysToggle.jsx";
import { combineDateAndTime, eachDateStrInRange } from "../../classroomSchedule.js";
import { clashNames } from "../../../../../utils/clashNames";

/* The lab card's Schedule: the quick route for booking a batch into this
   lab. Pick the batch; the dates, times and days arrive already filled in
   from the dashboard's filter (as applied), and can be changed here
   without going back to it. Saving is the same save the Bookings form
   makes on an existing batch: the batch keeps its name, course and trainer
   and takes this lab, those days, those dates and that time. */

const WEEK = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const BY_GETDAY = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

// A batch read from the list carries course and trainer either as the
// joined record (usually an array of one) or as a bare id.
const idOf = (v) => {
  const x = Array.isArray(v) ? v[0] : v;
  if (x && typeof x === "object") return String(x.id || x._id || "");
  return x ? String(x) : "";
};

/* The days to start with: the filter's Day wise when it has any, else the
   weekdays the dates actually fall on -- a single day is that one weekday,
   a week or more is all seven. Never empty when there is a date. */
const initialDays = ({ days, fromDate, toDate }) => {
  if (days && days.length) return WEEK.filter((d) => days.includes(d));
  if (!fromDate) return [];
  const dates = eachDateStrInRange(fromDate, toDate && toDate >= fromDate ? toDate : fromDate).slice(0, 7);
  const covered = new Set(dates.map((d) => BY_GETDAY[new Date(`${d}T00:00:00`).getDay()]));
  return WEEK.filter((d) => covered.has(d));
};

const ScheduleLabModal = ({ room, when, onClose, onSaved }) => {
  const { classroom } = room;

  const [batchId, setBatchId] = useState("");
  const [batch, setBatch] = useState(null);
  // Filled from the filter, then the user's to change.
  const [fromDate, setFromDate] = useState(when.fromDate || "");
  const [toDate, setToDate] = useState(when.toDate || when.fromDate || "");
  const [fromTime, setFromTime] = useState(when.fromTime || "");
  const [toTime, setToTime] = useState(when.toTime || "");
  const [days, setDays] = useState(() => initialDays(when));
  const [clashes, setClashes] = useState([]);
  const [saving, setSaving] = useState(false);

  const orderedDays = WEEK.filter((d) => days.includes(d));
  const startTime = combineDateAndTime(fromDate, fromTime);
  const endTime = combineDateAndTime(toDate || fromDate, toTime);

  // Each field says what is wrong under itself; Book stays off until none do.
  const errors = {
    dates: !fromDate
      ? "Pick a start date"
      : toDate && toDate < fromDate
        ? "The end date cannot be before the start date"
        : "",
    clock: !fromTime || !toTime
      ? "Pick a start and an end time"
      : toTime <= fromTime
        ? "The end time must be after the start time"
        : "",
    days: orderedDays.length ? "" : "Pick at least one day",
  };
  const ready = !errors.dates && !errors.clock && !errors.days && Boolean(startTime && endTime);
  // Same lab, same time: the API refuses the save, so Book stays off.
  const labClashes = clashes.filter((c) => c.lab);

  // The same overlap check the Bookings form runs, against this lab.
  useEffect(() => {
    if (!ready) {
      setClashes([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(() => {
      checkClash({
        classroom: classroom.id,
        days: orderedDays.join(","),
        startTime,
        endTime,
        excludeBatchId: batchId || undefined,
      })
        .then((result) => { if (!cancelled) setClashes(result || []); })
        .catch(() => { if (!cancelled) setClashes([]); });
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
    // orderedDays is rebuilt every render; its joined form is the dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, classroom.id, orderedDays.join(","), startTime, endTime, batchId]);

  const save = async () => {
    if (!ready || !batch || saving) return;
    setSaving(true);
    try {
      await updateBatch(batchId, {
        name: batch.name,
        courseId: idOf(batch.courseId),
        trainerId: idOf(batch.trainerId),
        classroom: classroom.id,
        days: orderedDays.join(","),
        startTime,
        endTime,
      });
      toast.success(`${batch.name || "Batch"} booked into ${classroom.classroomNo}`);
      onSaved();
    } catch (error) {
      toast.error(error?.response?.data?.message?.message || "Failed to book the batch");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      eyebrow="Schedule"
      title={classroom.classroomNo}
      sub={`${classroom.branchName || "No branch"}${classroom.floor ? ` · ${classroom.floor}` : ""}`}
      onClose={onClose}
      size="steady"
      labelledBy="cls-schedule-title"
      footer={
        <>
          <div className="pgm-actions">
            <button type="button" className="pgm-btn" onClick={onClose}>
              Cancel
            </button>
          </div>
          <button
            type="button"
            className="pg-btn pg-btn--main"
            disabled={!ready || !batchId || saving || labClashes.length > 0}
            onClick={save}
          >
            {saving ? "Booking..." : "Book batch"}
          </button>
        </>
      }
    >
      <div className="pg-field">
        <label className="pg-label" htmlFor="sl-batch">Batch</label>
        <div id="sl-batch">
          <AsyncPagedSelect
            value={batchId}
            onChange={(id, option) => {
              setBatchId(id || "");
              setBatch(option?.batch || null);
            }}
            fetchPage={async (page, search) => {
              const res = await getBatches(20, page, search, "name");
              return { data: res?.data || [], pagination: res?.pagination };
            }}
            toOption={(b) => ({ value: b.id, label: b.name || "Unnamed batch", batch: b })}
            placeholder="Search a batch"
          />
        </div>
      </div>

      {/* When -- filled in from the dashboard's filter, editable here. */}
      <section className="pgm-section cls-schedule-when">
        <h5 className="pgm-section-title">When</h5>

        <div className="mcq-options">
          <div className="pg-field">
            <label className="pg-label" htmlFor="sl-from-date">Start date</label>
            <input
              id="sl-from-date"
              type="date"
              className="pg-input"
              value={fromDate}
              onChange={(e) => {
                const v = e.target.value;
                setFromDate(v);
                if (toDate && v > toDate) setToDate(v);
              }}
            />
          </div>
          <div className="pg-field">
            <label className="pg-label" htmlFor="sl-to-date">End date</label>
            <input
              id="sl-to-date"
              type="date"
              className="pg-input"
              min={fromDate || undefined}
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
            />
            {errors.dates && <p className="att-error">{errors.dates}</p>}
          </div>
          <div className="pg-field">
            <label className="pg-label" htmlFor="sl-from-time">Start time</label>
            <input
              id="sl-from-time"
              type="time"
              className="pg-input"
              value={fromTime}
              onChange={(e) => setFromTime(e.target.value)}
            />
          </div>
          <div className="pg-field">
            <label className="pg-label" htmlFor="sl-to-time">End time</label>
            <input
              id="sl-to-time"
              type="time"
              className="pg-input"
              value={toTime}
              onChange={(e) => setToTime(e.target.value)}
            />
            {errors.clock && <p className="att-error">{errors.clock}</p>}
          </div>
        </div>

        <div className="pg-field">
          <label className="pg-label" htmlFor="sl-days">Days</label>
          <div id="sl-days">
            <DaysToggle value={days} onChange={setDays} />
          </div>
          {errors.days && <p className="att-error">{errors.days}</p>}
        </div>
      </section>

      {labClashes.length > 0 && (
        <p className="pg-notice pg-notice--bad">
          <AlertTriangle size={14} aria-hidden="true" />
          {classroom.classroomNo} is already booked at this time by{" "}
          {clashNames(labClashes)}. Pick another time or lab.
        </p>
      )}
    </Modal>
  );
};

export default ScheduleLabModal;
